import { useEffect, useMemo, useState } from 'react';
import { Modal } from '../ui/modal.jsx';
import { CountrySelect } from '../ui/country-select.jsx';
import { PickOnMapButton } from '../map/pick-on-map-button.jsx';
import { api, ApiError } from '../../api/index.js';

// Fallback used while the live tier list is loading or if the request
// fails. These are also the shipped server-side defaults in
// services/settings.js → DEFAULT_NODE_TIERS, so the modal still has a
// usable list even with the network momentarily down.
const FALLBACK_TIERS = [
  { value: 'HQ',    label: 'HQ — network root / management', defaultSidc: '10031002221100000000' },
  { value: 'C4ISR', label: 'C4ISR — full-featured command',  defaultSidc: '10031002211100000000' },
  { value: 'C2',    label: 'C2 — mid-tier command',          defaultSidc: '10031002181100000000' },
  { value: 'C1',    label: 'C1 — battalion / below',         defaultSidc: '10031002161100000000' },
];

const STATUSES = [
  { value: 'planned',         label: 'Planned',         description: 'Created but not yet operational.' },
  { value: 'active',          label: 'Active',          description: 'Live in the mesh; expected to be running.' },
  { value: 'suspended',       label: 'Suspended',       description: 'Temporarily disabled.' },
  { value: 'decommissioned',  label: 'Decommissioned',  description: 'Permanently retired; certs revoked.' },
];

const NAME_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{1,62}[A-Za-z0-9]$/;
const URL_RE = /^(https|wss):\/\/[^\s/$.?#].[^\s]*$/;
const SIDC_RE = /^\d{20}$/;

// Default SIDC-20 per tier comes from the dynamic tier list (Settings →
// Nodes). Helpers below derive sets / lookup tables from whatever
// `tiers` array we currently have.
function sidcMapFromTiers(tiers) {
  const out = {};
  for (const t of tiers || []) {
    if (t?.value && t?.defaultSidc) out[t.value] = t.defaultSidc;
  }
  return out;
}

const EMPTY_BASE = {
  name: '', tier: '', status: 'planned',
  url: '', description: '',
  countryCode: '', latitude: '', longitude: '',
  sidc20: '',
};
function emptyForTiers(tiers) {
  const fallback = tiers?.[0] || FALLBACK_TIERS[0];
  return {
    ...EMPTY_BASE,
    tier:   fallback?.value || 'C2',
    sidc20: fallback?.defaultSidc || '',
  };
}

function parseCoord(s) {
  if (s == null || s === '') return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
}

export function NodeFormModal({ open, mode, initial, onClose, onSave }) {
  const isCreate = mode === 'create';
  const [tiers, setTiers] = useState(FALLBACK_TIERS);
  const [values, setValues] = useState(() => emptyForTiers(FALLBACK_TIERS));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Pull the live tier list each time the modal opens. We don't cache
  // longer than that — operators expect Settings → Nodes changes to
  // show up the next time they pop a node form, without a page refresh.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    api.settings.system()
      .then((res) => {
        if (cancelled) return;
        const list = Array.isArray(res.settings.node_tiers) && res.settings.node_tiers.length > 0
          ? res.settings.node_tiers
          : FALLBACK_TIERS;
        setTiers(list);
      })
      .catch(() => {
        // Network blip — keep the fallback list, the form stays usable.
      });
    return () => { cancelled = true; };
  }, [open]);

  // The set of SIDC strings we recognise as "still a tier default" — used
  // below to decide whether typing in a new tier should auto-rewrite the
  // SIDC field.
  const knownDefaultSidcs = useMemo(
    () => new Set([...tiers, ...FALLBACK_TIERS].map((t) => t.defaultSidc).filter(Boolean)),
    [tiers],
  );

  useEffect(() => {
    if (!open) return;
    setError(null);
    setBusy(false);
    if (isCreate) {
      setValues(emptyForTiers(tiers));
      return;
    }
    const sidcMap = sidcMapFromTiers(tiers);
    const initialTier = initial?.tier || tiers[0]?.value || 'C2';
    setValues({
      name: initial?.name || '',
      tier: initialTier,
      status: initial?.status || 'planned',
      url: initial?.url || '',
      description: initial?.description || '',
      countryCode: initial?.countryCode || '',
      latitude: initial?.latitude != null ? String(initial.latitude) : '',
      longitude: initial?.longitude != null ? String(initial.longitude) : '',
      sidc20: initial?.sidc20 || sidcMap[initialTier] || '',
    });
    // tiers is read here only on open + initial; subsequent tier-list
    // refreshes don't clobber an in-progress edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, isCreate, initial]);

  // When the operator changes tier in CREATE mode, refresh the SIDC default
  // — but only if the current value still matches some default (don't clobber
  // a custom SIDC the operator already typed).
  useEffect(() => {
    if (!isCreate) return;
    const sidcMap = sidcMapFromTiers(tiers);
    setValues((prev) => {
      if (!knownDefaultSidcs.has(prev.sidc20)) return prev;
      return { ...prev, sidc20: sidcMap[prev.tier] || prev.sidc20 };
    });
  }, [values.tier, isCreate, tiers, knownDefaultSidcs]);

  const errors = useMemo(() => {
    const e = {};
    if (!NAME_RE.test(values.name)) {
      e.name = 'Name must be 3–64 chars, letters/digits/.-_, start and end alphanumeric.';
    }
    if (values.url && !URL_RE.test(values.url)) {
      e.url = 'URL must be https:// or wss://';
    }
    if (values.description && values.description.length > 1024) {
      e.description = 'Description must be at most 1024 characters.';
    }
    if (values.latitude !== '') {
      const n = parseCoord(values.latitude);
      if (Number.isNaN(n) || n < -90 || n > 90) e.latitude = 'Latitude must be in [-90, 90].';
    }
    if (values.longitude !== '') {
      const n = parseCoord(values.longitude);
      if (Number.isNaN(n) || n < -180 || n > 180) e.longitude = 'Longitude must be in [-180, 180].';
    }
    // If one of lat/lon is set, both must be set.
    if ((values.latitude === '') !== (values.longitude === '')) {
      if (values.latitude === '') e.latitude = 'Both coordinates required.';
      if (values.longitude === '') e.longitude = 'Both coordinates required.';
    }
    if (values.sidc20 && !SIDC_RE.test(values.sidc20)) {
      e.sidc20 = 'SIDC-20 must be exactly 20 digits.';
    }
    return e;
  }, [values]);

  const canSubmit = Object.keys(errors).length === 0 && !busy;

  function update(field, v) {
    setValues((prev) => ({ ...prev, [field]: v }));
  }

  async function handleSubmit(e) {
    e?.preventDefault?.();
    if (!canSubmit) return;
    setBusy(true);
    setError(null);
    try {
      await onSave({
        name: values.name.trim(),
        tier: values.tier,
        status: values.status,
        url: values.url.trim() || null,
        description: values.description.trim() || null,
        countryCode: values.countryCode || null,
        latitude: values.latitude === '' ? null : Number(values.latitude),
        longitude: values.longitude === '' ? null : Number(values.longitude),
        sidc20: values.sidc20 ? values.sidc20.trim() : null,
      });
    } catch (err) {
      setError(err instanceof ApiError ? err.payload?.error || err.message : err?.message || 'Operation failed.');
      setBusy(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={busy ? () => {} : onClose}
      title={isCreate ? 'Create node' : `Edit ${initial?.name || 'node'}`}
      description={
        isCreate
          ? 'Register a new middle-tier mesh node. Issue its certificate after creation.'
          : 'Update node metadata. Renaming does not affect already-issued certificates.'
      }
      size="lg"
      footer={
        <>
          <button type="button" onClick={onClose} disabled={busy} className="btn-secondary" data-skip-autofocus>
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} disabled={!canSubmit} className="btn-primary">
            {busy ? (
              <>
                <span className="loader-spinner" aria-hidden="true" /> Saving…
              </>
            ) : (
              <>
                <i className={`fa-solid ${isCreate ? 'fa-plus' : 'fa-floppy-disk'}`} aria-hidden="true" />
                {isCreate ? 'Create node' : 'Save changes'}
              </>
            )}
          </button>
        </>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-4" noValidate>
        {error && (
          <div role="alert" className="rounded-2xl border border-rose-200 bg-rose-soft px-4 py-3 text-sm text-rose-700">
            <i className="fa-solid fa-circle-exclamation mr-2" aria-hidden="true" />
            {error}
          </div>
        )}

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <label htmlFor="node-name" className="field-label">Name</label>
            <input
              id="node-name"
              className="field font-mono text-[13px]"
              placeholder="c2-bratislava"
              value={values.name}
              onChange={(e) => update('name', e.target.value)}
              autoComplete="off"
              spellCheck="false"
              disabled={busy}
            />
            {errors.name && <p className="mt-1 text-xs text-rose-600">{errors.name}</p>}
          </div>
          <div>
            <label htmlFor="node-tier" className="field-label">Tier</label>
            <select
              id="node-tier"
              className="field"
              value={values.tier}
              onChange={(e) => update('tier', e.target.value)}
              disabled={busy}
            >
              {tiers.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
              {/* If we're editing a node whose tier was removed from the
                  list (shouldn't happen — server refuses such edits —
                  but be defensive), keep it visible with an "(orphan)"
                  marker so the operator can choose a replacement. */}
              {!tiers.some((t) => t.value === values.tier) && values.tier && (
                <option value={values.tier}>
                  {values.tier} (not in current list)
                </option>
              )}
            </select>
          </div>
        </div>

        <div>
          <label htmlFor="node-url" className="field-label">
            URL <span className="font-normal text-mist">(optional, https:// or wss://)</span>
          </label>
          <input
            id="node-url"
            className="field font-mono text-[13px]"
            placeholder="https://c2-brat.example.mil:8443"
            value={values.url}
            onChange={(e) => update('url', e.target.value)}
            autoComplete="off"
            spellCheck="false"
            disabled={busy}
          />
          {errors.url && <p className="mt-1 text-xs text-rose-600">{errors.url}</p>}
        </div>

        {/* Country + coordinates (with map picker) */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div>
            <label htmlFor="node-country" className="field-label">
              Country <span className="font-normal text-mist">(optional)</span>
            </label>
            <CountrySelect
              id="node-country"
              value={values.countryCode || null}
              onChange={(v) => update('countryCode', v || '')}
              disabled={busy}
            />
          </div>

          {/* Coordinates sub-grid spans 2 columns and gets its own header row
              so the Pick-on-map button sits directly above the lat/lon
              inputs it operates on. */}
          <div className="sm:col-span-2">
            <div className="mb-1.5 flex items-center justify-between">
              <span className="field-label !mb-0">Coordinates (°)</span>
              <PickOnMapButton
                latitude={values.latitude}
                longitude={values.longitude}
                title={values.name ? `Pick location for ${values.name}` : 'Pick node location'}
                disabled={busy}
                onChange={(lat, lon) => {
                  setValues((p) => ({
                    ...p,
                    latitude:  lat.toFixed(6),
                    longitude: lon.toFixed(6),
                  }));
                }}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <input
                  id="node-lat"
                  type="number"
                  step="any"
                  min="-90"
                  max="90"
                  className="field font-mono text-[13px]"
                  placeholder="Latitude  e.g. 49.5500"
                  value={values.latitude}
                  onChange={(e) => update('latitude', e.target.value)}
                  disabled={busy}
                />
                {errors.latitude && <p className="mt-1 text-xs text-rose-600">{errors.latitude}</p>}
              </div>
              <div>
                <input
                  id="node-lon"
                  type="number"
                  step="any"
                  min="-180"
                  max="180"
                  className="field font-mono text-[13px]"
                  placeholder="Longitude  e.g. 25.5900"
                  value={values.longitude}
                  onChange={(e) => update('longitude', e.target.value)}
                  disabled={busy}
                />
                {errors.longitude && <p className="mt-1 text-xs text-rose-600">{errors.longitude}</p>}
              </div>
            </div>
          </div>
        </div>

        <div>
          <label className="field-label">Status</label>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
            {STATUSES.map((s) => {
              const active = values.status === s.value;
              return (
                <label
                  key={s.value}
                  className={
                    'flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 transition-colors ' +
                    (active ? 'border-accent bg-accent-soft' : 'border-line bg-white hover:bg-canvas')
                  }
                >
                  <input
                    type="radio"
                    name="node-status"
                    value={s.value}
                    checked={active}
                    onChange={() => update('status', s.value)}
                    className="mt-1 h-4 w-4 accent-accent"
                    disabled={busy}
                  />
                  <span>
                    <span className="block text-sm font-semibold text-ink">{s.label}</span>
                    <span className="block text-xs text-mist">{s.description}</span>
                  </span>
                </label>
              );
            })}
          </div>
        </div>

        <div>
          <label htmlFor="node-sidc" className="field-label">
            SIDC-20 <span className="font-normal text-mist">(MIL-STD-2525E / APP-6D)</span>
          </label>
          <input
            id="node-sidc"
            className="field font-mono text-[13px]"
            placeholder="10031002181100000000"
            value={values.sidc20}
            onChange={(e) => update('sidc20', e.target.value.replace(/\D/g, '').slice(0, 20))}
            inputMode="numeric"
            maxLength={20}
            autoComplete="off"
            spellCheck="false"
            disabled={busy}
          />
          <p className="mt-1 text-xs text-mist">
            20-digit symbol code rendered on the map. Default reflects the
            tier ({values.tier} = {values.tier === 'C1' ? 'Battalion HQ' : values.tier === 'C2' ? 'Brigade HQ' : 'Division HQ'}).
          </p>
          {errors.sidc20 && <p className="mt-1 text-xs text-rose-600">{errors.sidc20}</p>}
        </div>

        <div>
          <label htmlFor="node-desc" className="field-label">
            Description <span className="font-normal text-mist">(optional)</span>
          </label>
          <textarea
            id="node-desc"
            className="field min-h-[80px] resize-y"
            value={values.description}
            onChange={(e) => update('description', e.target.value)}
            maxLength={1024}
            disabled={busy}
          />
          {errors.description && <p className="mt-1 text-xs text-rose-600">{errors.description}</p>}
        </div>
      </form>
    </Modal>
  );
}
